/*
  Smooth open and close for the product details accordion.

  WHY THIS NEEDS SCRIPT AT ALL

  <details> is all-or-nothing. The moment `open` is set the content is laid
  out at full height, and the moment it is removed the content stops being
  rendered — so there is no in-between for CSS to transition through. The
  usual CSS-only trick, animating grid-template-rows from 0fr to 1fr, gets
  the opening half right and still slams shut, because by the time the row
  could shrink the browser has already stopped painting what was inside it.

  So the element is held open for the length of the closing animation and
  only then actually closed.

  WHAT IS NOT REPLACED

  The exclusive behaviour — one row open at a time — is the name attribute
  doing its job in the browser, and it stays. This only borrows the moment
  between click and state change; everything else about <details> is left
  alone, including keyboard operation and the open state being real to
  find-in-page and to the accessibility tree.
*/
(() => {
  const ITEM = 'details.dtl__item';
  /*
    Opening decelerates into place; closing accelerates away. One curve used
    both ways is what makes an accordion feel mechanical — a drawer does not
    come back the way it went.
  */
  const EASE_OPEN = 'cubic-bezier(0.33, 1, 0.68, 1)';
  const EASE_CLOSE = 'cubic-bezier(0.32, 0, 0.67, 0)';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
    Read from the stylesheet rather than hard-coded, so the duration setting
    on the section is the only place the number lives.
  */
  function duration(item) {
    const raw = getComputedStyle(item).getPropertyValue('--dtl-open-duration').trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 320;
    return raw.endsWith('ms') || !raw.endsWith('s') ? value : value * 1000;
  }

  function body(item) {
    return item.querySelector('.dtl__body');
  }

  /*
    THE PADDING HAS TO COME WITH IT.

    .dtl__body carries a bottom padding from the row_padding setting. Animate
    only the height and that padding is still there at the end — the panel
    bottoms out at nineteen-odd pixels instead of nothing, sits there for a
    frame, and then vanishes when `open` is finally removed. That last
    disappearance is the snag: the row appears to catch, then drop.

    So the padding travels with the height and both arrive at zero together.

    The text fades on its own schedule rather than tracking the height. Tied
    to it, the content stays fully opaque while the box crushes it, which
    reads as squashing; leaving early on the way out and arriving late on the
    way in reads as content moving out of and into a space.
  */
  function animate(panel, from, to, ms, onDone) {
    const opening = to > from;
    const pad = getComputedStyle(panel).paddingBottom;

    panel.style.overflow = 'hidden';
    const run = panel.animate(
      [
        {
          height: `${from}px`,
          paddingBottom: opening ? '0px' : pad,
          opacity: opening ? 0 : 1,
        },
        {
          opacity: opening ? 1 : 0,
          offset: opening ? 0.7 : 0.45,
        },
        {
          height: `${to}px`,
          paddingBottom: opening ? pad : '0px',
          opacity: opening ? 1 : 0,
        },
      ],
      {
        duration: ms,
        easing: opening ? EASE_OPEN : EASE_CLOSE,
        /*
          Closing holds its last frame. Without it the animation ends, the
          panel springs back to its natural full height for the one frame
          before `open` is removed, and that flash is a worse snag than the
          one this whole function exists to remove. Opening needs no fill —
          its end state IS the natural one.
        */
        fill: opening ? 'none' : 'forwards',
      }
    );
    run.onfinish = () => {
      panel.style.overflow = '';
      onDone?.();
      // Released only after `open` is gone, so the fill never outlives the
      // thing it was covering for.
      if (!opening) run.cancel();
    };
    // A cancelled animation still has to clean up, or an interrupted row is
    // left clipped at whatever height it had reached.
    run.oncancel = () => {
      panel.style.overflow = '';
    };
    return run;
  }

  function open(item) {
    const panel = body(item);
    item.open = true;
    if (!panel || reduced.matches) return;
    item.__dtlAnim?.cancel();
    item.__dtlAnim = animate(panel, 0, panel.scrollHeight, duration(item));
  }

  function close(item) {
    const panel = body(item);
    if (!panel || reduced.matches) {
      item.open = false;
      return;
    }
    item.__dtlAnim?.cancel();
    // Held open until the animation finishes; closing first would remove the
    // very thing being animated.
    item.__dtlAnim = animate(panel, panel.scrollHeight, 0, duration(item), () => {
      item.open = false;
    });
  }

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.dtl__summary');
    if (!summary) return;

    const item = summary.closest(ITEM);
    if (!item) return;

    event.preventDefault();

    if (item.open) {
      close(item);
      return;
    }

    /*
      The name attribute closes the sibling itself, instantly, the moment this
      one opens — which would leave one row sliding open while another
      vanished. Closed here first, with the same animation, so the pair reads
      as one movement.
    */
    const group = item.getAttribute('name');
    if (group) {
      document
        .querySelectorAll(`${ITEM}[name="${CSS.escape(group)}"][open]`)
        .forEach((sibling) => {
          if (sibling !== item) close(sibling);
        });
    }

    open(item);
  });
})();
